import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, LineChart, Line } from 'recharts';

// Import des widgets
import PerformanceCard from './widgets/PerformanceCard';
import CircularGauge from './widgets/CircularGauge';
import ProgressBar from './widgets/ProgressBar';
import MiniChart from './widgets/MiniChart';
import AlertBadge from './widgets/AlertBadge';
import RecouvrementSection from './widgets/RecouvrementSection';
import FinanceSection from './widgets/FinanceSection';
import OperationsSection from './widgets/OperationsSection';
import IncidentsRetardsSection from './widgets/IncidentsRetardsSection';

import './css/BaobabDesignSystem.css';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const EnhancedDashboard = ({ onNavigate, activeTab = 'overview' }) => {
  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [animationComplete, setAnimationComplete] = useState(false);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  useEffect(() => {
    // Animation de chargement des chiffres
    if (dashboardData) {
      setTimeout(() => setAnimationComplete(true), 500);
    }
  }, [dashboardData]);

  const fetchDashboardData = async () => {
    try {
      const response = await axios.get(`${API}/dashboard/enhanced-v2`);
      setDashboardData(response.data);
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('fr-FR').format(amount) + ' FCFA';
  };

  const handleWidgetClick = (section, data) => {
    console.log('Widget clicked:', section, data);
    // Navigation ou modal selon le widget cliqué
    if (onNavigate) {
      switch (section) {
        case 'transporteurs':
          onNavigate('transporteurs');
          break;
        case 'operations':
          onNavigate('operations');
          break;
        case 'opportunities':
          onNavigate('opportunities');
          break;
        case 'documents':
          onNavigate('documents');
          break;
        default:
          break;
      }
    }
  };

  if (loading) {
    return (
      <div className="p-6">
        <div className="text-center">
          <div className="spinner mb-4"></div>
          <p>Chargement du tableau de bord amélioré...</p>
        </div>
      </div>
    );
  }

  if (!dashboardData) {
    return (
      <div className="p-6">
        <div className="text-center text-gray-500">
          <div className="text-4xl mb-4">📊</div>
          <p>Impossible de charger les données du tableau de bord</p>
        </div>
      </div>
    );
  }

  const { 
    performance_globale, 
    transporteurs_kpis, 
    operations_avance, 
    commercial_avance, 
    factures_recouvrement, 
    qualite_satisfaction,
    finance_kpis,
    operations_advanced_kpis
  } = dashboardData;

  const renderTabContent = () => {
    switch (activeTab) {
      case 'finance':
        return (
          <FinanceSection 
            data={finance_kpis} 
            onNavigate={handleWidgetClick}
            formatCurrency={formatCurrency}
          />
        );
      case 'operations':
        return (
          <OperationsSection 
            data={operations_advanced_kpis} 
            onNavigate={handleWidgetClick}
            formatCurrency={formatCurrency}
          />
        );
      case 'incidents':
        return (
          <IncidentsRetardsSection 
            data={{
              ...operations_advanced_kpis,
              ...operations_avance  // Combiner les données des deux sources
            }} 
            onNavigate={handleWidgetClick}
            formatCurrency={formatCurrency}
          />
        );
      default:
        return renderOverviewTab();
    }
  };

  const renderOverviewTab = () => (
    <div className="space-y-8">
      {/* Bloc "Performance Globale" */}
      <section className="bg-white rounded-xl shadow-lg border border-gray-200 p-6">
        <h2 className="text-2xl font-bold text-green-700 mb-6 flex items-center">
          <span className="mr-3">🚀</span>
          Performance Globale
        </h2>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-6">
          <PerformanceCard
            title="Chiffre d'Affaires Cumulé"
            value={performance_globale.chiffre_affaires_cumule}
            unit="FCFA"
            change={`${performance_globale.chiffre_affaires_evolution >= 0 ? '+' : ''}${performance_globale.chiffre_affaires_evolution.toFixed(1)}%`}
            changeLabel="ce mois"
            icon="💰"
            color="green"
            animateValue={!animationComplete}
            onClick={() => handleWidgetClick('revenue', performance_globale)}
          />
          
          <PerformanceCard
            title="Marge Cumulée"
            value={formatCurrency(performance_globale.benefice_cumule)}
            unit=""
            icon="💎"
            color="blue"
            animateValue={!animationComplete}
            onClick={() => handleWidgetClick('profit', performance_globale)}
          />
          
          <div className="bg-purple-50 border-2 border-purple-200 rounded-xl p-6 flex flex-col items-center justify-center">
            <CircularGauge
              title="Marge Moyenne"
              value={performance_globale.marge_moyenne_pourcentage}
              max={30}
              size={100}
              color="#8B5CF6"
              unit="%"
            />
          </div>
          
          <PerformanceCard
            title="CA Prévisionnel"
            value={performance_globale.ca_previsionnel}
            unit="FCFA"
            change="Opportunités chaudes"
            changeLabel="en pipeline"
            icon="🔮"
            color="purple"
            animateValue={!animationComplete}
            onClick={() => handleWidgetClick('forecast', performance_globale)}
          />
        </div>
        
        <div className="bg-gradient-to-r from-green-50 to-green-100 rounded-lg p-4">
          <ProgressBar
            title={`Vous avez atteint ${performance_globale.objectif_mensuel_progress.toFixed(0)}% de votre objectif mensuel !`}
            progress={performance_globale.objectif_mensuel_progress}
            current={dashboardData.dashboard_stats.revenue_this_month}
            target={5000000} // Exemple: objectif de 5M par mois
            unit="FCFA"
            color={performance_globale.objectif_mensuel_atteint ? '#22C55E' : '#F97316'}
            height={12}
          />
        </div>
      </section>

      {/* Bloc "Suivi Transporteurs" */}
      <section className="bg-white rounded-xl shadow-lg border border-gray-200 p-6">
        <h2 className="text-2xl font-bold text-green-700 mb-6 flex items-center">
          <span className="mr-3">🚚</span>
          Suivi Transporteurs
        </h2>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <PerformanceCard
            title="Total Paiements Transporteurs"
            value={transporteurs_kpis.total_paiements_transporteurs}
            unit="FCFA"
            changeLabel="versé ce mois-ci"
            icon="🚛"
            color="blue"
            onClick={() => handleWidgetClick('transporteurs', transporteurs_kpis)}
          />
          
          <AlertBadge
            type={transporteurs_kpis.avances_versees > 1000000 ? "warning" : "neutral"}
            title="Avances versées"
            description={formatCurrency(transporteurs_kpis.avances_versees)}
            count={transporteurs_kpis.avances_versees > 1000000 ? 1 : 0}
            icon=""
            pulse={transporteurs_kpis.avances_versees > 1000000}
          />
          
          <AlertBadge
            type={transporteurs_kpis.transporteurs_en_attente_paiement > 5 ? "warning" : "success"}
            title="Transporteurs en attente"
            description="de paiement"
            count={transporteurs_kpis.transporteurs_en_attente_paiement}
            icon="⏰"
            onClick={() => handleWidgetClick('payment-pending', transporteurs_kpis)}
          />
          
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
            <h4 className="text-sm font-medium text-gray-700 mb-3">Tarifs Transporteurs</h4>
            <div className="space-y-2">
              <div className="flex justify-between">
                <span className="text-xs text-gray-500">Minimum</span>
                <span className="text-sm font-medium">{transporteurs_kpis.tarif_min.toLocaleString()} FCFA</span>
              </div>
              <div className="flex justify-between">
                <span className="text-xs text-gray-500">Moyen</span>
                <span className="text-sm font-medium text-blue-600">{transporteurs_kpis.tarif_moyen.toLocaleString()} FCFA</span>
              </div>
              <div className="flex justify-between">
                <span className="text-xs text-gray-500">Maximum</span>
                <span className="text-sm font-medium">{transporteurs_kpis.tarif_max.toLocaleString()} FCFA</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Bloc "Opérations" avec suivi incidents et retards */}
      <section className="bg-white rounded-xl shadow-lg border border-gray-200 p-6">
        <h2 className="text-2xl font-bold text-green-700 mb-6 flex items-center">
          <span className="mr-3">📦</span>
          Opérations - Suivi Incidents & Retards
        </h2>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-6">
          <AlertBadge
            type={operations_avance.operations_jour_actuelles >= operations_avance.objectif_operations_jour ? "success" : "warning"}
            title="Objectif Opérations du Jour"
            description={`${operations_avance.operations_jour_actuelles}/${operations_avance.objectif_operations_jour} opérations`}
            count={operations_avance.operations_jour_actuelles >= operations_avance.objectif_operations_jour ? 0 : operations_avance.objectif_operations_jour - operations_avance.operations_jour_actuelles}
            icon={operations_avance.operations_jour_actuelles >= operations_avance.objectif_operations_jour ? "✅" : "🎯"}
            onClick={() => handleWidgetClick('operations', operations_avance)}
          />
          
          {/* NOUVEAUX WIDGETS INCIDENTS */}
          <AlertBadge
            type={operations_avance.incidents_jour > 2 ? "error" : operations_avance.incidents_jour > 0 ? "warning" : "success"}
            title="Incidents Aujourd'hui"
            description={`${operations_avance.incidents_jour} incident${operations_avance.incidents_jour > 1 ? 's' : ''}`}
            count={operations_avance.incidents_jour}
            icon="🚨"
            pulse={operations_avance.incidents_jour > 2}
            onClick={() => handleWidgetClick('incidents-today', operations_avance)}
          />
          
          <AlertBadge
            type={operations_avance.operations_en_retard > 3 ? "error" : operations_avance.operations_en_retard > 1 ? "warning" : "success"}
            title="Opérations en Retard"
            description="Dépassent délai prévu"
            count={operations_avance.operations_en_retard}
            icon="⏰"
            pulse={operations_avance.operations_en_retard > 3}
            onClick={() => handleWidgetClick('operations-delayed', operations_avance)}
          />
          
          <PerformanceCard
            title="Nombre Opérations"
            value={operations_avance.operations_mois}
            changeLabel="ce mois"
            change={operations_avance.incidents_mois > 0 ? `${operations_avance.incidents_mois} incident${operations_avance.incidents_mois > 1 ? 's' : ''}` : "Aucun incident"}
            icon="📋"
            color={operations_avance.incidents_mois === 0 ? "green" : operations_avance.incidents_mois < 5 ? "yellow" : "red"}
          />
        </div>
        
        {/* Alertes spéciales pour incidents critiques */}
        {operations_avance.incidents_jour > 3 && (
          <div className="bg-red-50 border-l-4 border-red-400 p-4 mb-6">
            <div className="flex">
              <div className="flex-shrink-0">
                <span className="text-red-400 text-xl">🚨</span>
              </div>
              <div className="ml-3">
                <p className="text-sm text-red-700">
                  <strong>Alerte :</strong> Nombre élevé d'incidents aujourd'hui ({operations_avance.incidents_jour}). 
                  Une intervention immédiate pourrait être nécessaire.
                </p>
                <p className="text-xs text-red-600 mt-1">
                  Cliquez sur "Incidents Aujourd'hui" pour voir les détails.
                </p>
              </div>
            </div>
          </div>
        )}
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {operations_avance.operation_plus_rentable && (
            <AlertBadge
              type="success"
              title="Opération la plus rentable"
              description={`${operations_avance.operation_plus_rentable.nom} - ${formatCurrency(operations_avance.operation_plus_rentable.valeur)}`}
              icon={operations_avance.operation_plus_rentable.has_incident ? "🏆" : "🏆"}
              onClick={() => handleWidgetClick('best-operation', operations_avance.operation_plus_rentable)}
            />
          )}
          
          {operations_avance.operation_moins_rentable && (
            <AlertBadge
              type="warning"
              title="Opération la moins rentable"
              description={`${operations_avance.operation_moins_rentable.nom} - ${formatCurrency(operations_avance.operation_moins_rentable.valeur)}`}
              icon={operations_avance.operation_moins_rentable.has_incident ? "🚨" : ""}
              onClick={() => handleWidgetClick('worst-operation', operations_avance.operation_moins_rentable)}
            />
          )}
        </div>
      </section>

      {/* Bloc "Commercial" */}
      <section className="bg-white rounded-xl shadow-lg border border-gray-200 p-6">
        <h2 className="text-2xl font-bold text-green-700 mb-6 flex items-center">
          <span className="mr-3">💼</span>
          Commercial
        </h2>
        
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* CA par Commercial */}
          <div className="lg:col-span-2">
            <h3 className="text-lg font-semibold text-gray-800 mb-4">CA par Commercial</h3>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={commercial_avance.ca_par_commercial}>
                  <XAxis dataKey="nom" />
                  <YAxis />
                  <Tooltip 
                    formatter={(value) => [formatCurrency(value), 'CA']}
                    labelStyle={{ color: '#374151' }}
                    contentStyle={{
                      backgroundColor: 'white',
                      border: '1px solid #E5E7EB',
                      borderRadius: '8px'
                    }}
                  />
                  <Bar dataKey="ca" fill="#22C55E" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
          
          {/* Taux de conversion et CA par client */}
          <div className="space-y-6">
            <div className="text-center">
              <CircularGauge
                title="Taux de Conversion"
                value={commercial_avance.taux_conversion}
                size={120}
                color="#3B82F6"
                unit="%"
              />
            </div>
            
            <div>
              <h3 className="text-lg font-semibold text-gray-800 mb-4">Top Clients (CA)</h3>
              <div className="space-y-2 max-h-40 overflow-y-auto">
                {commercial_avance.ca_par_client.slice(0, 5).map((client, index) => (
                  <div key={index} className="flex justify-between items-center p-2 bg-gray-50 rounded-lg">
                    <span className="text-sm font-medium truncate flex-1">{client.nom}</span>
                    <span className="text-sm text-green-600 font-bold ml-2">
                      {client.ca.toLocaleString('fr-FR')} FCFA
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Bloc "Factures & Recouvrement" - Version détaillée */}
      <RecouvrementSection 
        data={factures_recouvrement} 
        onNavigate={handleWidgetClick}
        formatCurrency={formatCurrency}
      />

      {/* Bloc "Qualité & Satisfaction" */}
      <section className="bg-white rounded-xl shadow-lg border border-gray-200 p-6">
        <h2 className="text-2xl font-bold text-green-700 mb-6 flex items-center">
          <span className="mr-3">⭐</span>
          Qualité & Satisfaction
        </h2>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="text-center">
            <div className="text-6xl mb-2">
              {qualite_satisfaction.taux_satisfaction_moyenne >= 4.5 ? '😊' : 
               qualite_satisfaction.taux_satisfaction_moyenne >= 3.5 ? '😐' : '😞'}
            </div>
            <div className="text-2xl font-bold text-green-600">
              {qualite_satisfaction.taux_satisfaction_moyenne.toFixed(1)}/5
            </div>
            <p className="text-sm text-gray-600">Taux de satisfaction</p>
          </div>
          
          <div className="text-center">
            <CircularGauge
              title="Taux de Churn"
              value={qualite_satisfaction.taux_churn}
              size={100}
              color={qualite_satisfaction.taux_churn > 10 ? '#EF4444' : '#22C55E'}
              unit="%"
            />
          </div>
          
          <AlertBadge
            type={qualite_satisfaction.reclamations_ouvertes > 5 ? "warning" : "neutral"}
            title="Réclamations"
            description={`${qualite_satisfaction.reclamations_ouvertes} ouvertes sur ${qualite_satisfaction.nombre_reclamations} total`}
            count={qualite_satisfaction.reclamations_ouvertes}
            icon="📧"
            onClick={() => handleWidgetClick('complaints', qualite_satisfaction)}
          />
        </div>
      </section>
    </div>
  );

  return (
    <div className="p-6 space-y-8">
      {/* En-tête du tableau de bord */}
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-gray-900 flex items-center">
          <span className="mr-3">📊</span>
          Tableau de Bord - Baobab Connect
        </h1>
        <p className="text-gray-600 mt-2">
          {activeTab === 'finance' && 'Vision comptable et stratégique de votre activité'}
          {activeTab === 'operations' && 'Suivi logistique et gestion des incidents'}
          {activeTab === 'incidents' && 'Analyse détaillée des incidents et gestion des retards'}
          {activeTab === 'overview' && 'Vue d\'ensemble de votre activité commerciale et opérationnelle'}
        </p>
      </div>

      {/* Contenu de l'onglet actif */}
      {renderTabContent()}
    </div>
  );
};

export default EnhancedDashboard;